import { create } from 'zustand';
import type { Project, Task, SpecDocument, Workflow, Prototype } from '@/types';

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  tasks: Task[];
  specDocument: SpecDocument | null;
  workflow: Workflow | null;
  prototype: Prototype | null;
  isLoading: boolean;
  error: string | null;
}

interface ProjectActions {
  setProjects: (projects: Project[]) => void;
  addProject: (project: Project) => void;
  updateProject: (projectId: string, updates: Partial<Project>) => void;
  deleteProject: (projectId: string) => void;
  setCurrentProject: (project: Project | null) => void;
  setTasks: (tasks: Task[]) => void;
  addTask: (task: Task) => void;
  updateTask: (taskId: string, updates: Partial<Task>) => void;
  deleteTask: (taskId: string) => void;
  setSpecDocument: (doc: SpecDocument | null) => void;
  updateSpecDocument: (content: string) => void;
  setWorkflow: (workflow: Workflow | null) => void;
  updateWorkflow: (updates: Partial<Workflow>) => void;
  setPrototype: (prototype: Prototype | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

type ProjectStore = ProjectState & ProjectActions;

const initialState: ProjectState = {
  projects: [],
  currentProject: null,
  tasks: [],
  specDocument: null,
  workflow: null,
  prototype: null,
  isLoading: false,
  error: null,
};

export const useProjectStore = create<ProjectStore>((set) => ({
  ...initialState,

  setProjects: (projects) =>
    set({
      projects,
    }),

  addProject: (project) =>
    set((state) => ({
      projects: [...state.projects, project],
    })),

  updateProject: (projectId, updates) =>
    set((state) => ({
      projects: state.projects.map((p) =>
        p.id === projectId ? { ...p, ...updates } : p
      ),
      currentProject:
        state.currentProject?.id === projectId
          ? { ...state.currentProject, ...updates }
          : state.currentProject,
    })),

  deleteProject: (projectId) =>
    set((state) => ({
      projects: state.projects.filter((p) => p.id !== projectId),
      currentProject:
        state.currentProject?.id === projectId ? null : state.currentProject,
    })),

  setCurrentProject: (project) =>
    set({
      currentProject: project,
    }),

  setTasks: (tasks) =>
    set({
      tasks,
    }),

  addTask: (task) =>
    set((state) => ({
      tasks: [...state.tasks, task],
    })),

  updateTask: (taskId, updates) =>
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.id === taskId ? { ...t, ...updates } : t
      ),
    })),

  deleteTask: (taskId) =>
    set((state) => ({
      tasks: state.tasks.filter((t) => t.id !== taskId),
    })),

  setSpecDocument: (doc) =>
    set({
      specDocument: doc,
    }),

  updateSpecDocument: (content) =>
    set((state) => ({
      specDocument: state.specDocument
        ? {
            ...state.specDocument,
            content,
            version: state.specDocument.version + 1,
            updatedAt: new Date().toISOString(),
          }
        : null,
    })),

  setWorkflow: (workflow) =>
    set({
      workflow,
    }),

  updateWorkflow: (updates) =>
    set((state) => ({
      workflow: state.workflow
        ? {
            ...state.workflow,
            ...updates,
            updatedAt: new Date().toISOString(),
          }
        : null,
    })),

  setPrototype: (prototype) =>
    set({
      prototype,
    }),

  setLoading: (isLoading) =>
    set({
      isLoading,
    }),

  setError: (error) =>
    set({
      error,
    }),

  reset: () => set(initialState),
}));
